import { cn } from "@/components/utils/utils";

/**
 * Una scelta del selettore di periodo.
 *
 * `label` e' quello che si legge sulla pill: va tenuto corto (7g, 90g, YTD), altrimenti
 * il gruppo sfonda la riga su mobile. Quando l'etichetta e' abbreviata al punto da non
 * spiegarsi da sola, `title` porta la versione estesa a tooltip e screen reader.
 */
export interface PeriodFilterOption<T extends string | number> {
  value: T;
  label: string;
  title?: string;
}

interface PeriodFilterProps<T extends string | number> {
  options: readonly PeriodFilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Descrive di cosa e' il periodo: le pill da sole non lo dicono. */
  ariaLabel?: string;
  className?: string;
}

/**
 * Selettore di periodo a pill, condiviso fra Panoramica e Pagamenti.
 *
 * Volutamente agnostico sui valori: la panoramica ragiona in periodi passati ('7d',
 * 'ytd'), i pagamenti in giorni di finestra in avanti (7, 30, 0 = tutto). E' la stessa
 * interazione, non lo stesso dominio, quindi le opzioni le passa il chiamante.
 */
export const PeriodFilter = <T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: PeriodFilterProps<T>) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className={cn(
      "inline-flex items-center bg-gray-100 rounded-2xl p-1 gap-0.5",
      className
    )}
  >
    {options.map((option) => (
      <button
        key={String(option.value)}
        type="button"
        onClick={() => onChange(option.value)}
        aria-pressed={option.value === value}
        title={option.title}
        aria-label={option.title}
        className={
          option.value === value
            ? "px-3 py-1.5 text-sm font-medium text-gray-900 bg-white rounded-xl shadow-sm transition-all"
            : "px-3 py-1.5 text-sm text-gray-500 rounded-xl hover:text-gray-700 transition-all"
        }
      >
        {option.label}
      </button>
    ))}
  </div>
);
